/**
 * Admin form builder.
 *
 * Drives ``document_templates.form_schema``. Lists the fields,
 * provides per-field config (label / required / help text / options
 * for choice fields), and adds new fields via a dropdown of types.
 *
 * Reordering uses up/down arrows in v1 — drag-and-drop is a Tier-2
 * polish item that doesn't change the schema shape.
 *
 * Pure controlled component: parent owns ``schema`` state and gets
 * the new array via ``onChange`` on every edit.
 */
import { useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  GripVertical,
  Plus,
  Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import {
  FIELD_TYPES,
  FIELD_TYPE_LABELS,
  FIELDS_WITH_OPTIONS,
  newFieldId,
  type FieldType,
  type FormField,
} from "./types";

interface FormBuilderProps {
  schema: FormField[];
  onChange: (next: FormField[]) => void;
}

export function FormBuilder({ schema, onChange }: FormBuilderProps) {
  const [addOpen, setAddOpen] = useState(false);

  const updateField = (idx: number, patch: Partial<FormField>) => {
    onChange(
      schema.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    );
  };

  const removeField = (idx: number) => {
    onChange(schema.filter((_, i) => i !== idx));
  };

  const moveField = (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    if (next < 0 || next >= schema.length) return;
    const arr = [...schema];
    [arr[idx], arr[next]] = [arr[next], arr[idx]];
    onChange(arr);
  };

  const addField = (type: FieldType) => {
    setAddOpen(false);
    const defaults: Record<FieldType, Partial<FormField>> = {
      text: { label: "Text field" },
      textarea: { label: "Notes" },
      date: { label: "Date" },
      select: { label: "Choose one", options: ["Option A", "Option B"] },
      checkbox: { label: "I agree" },
      checkbox_group: {
        label: "Select all that apply",
        options: ["Option A", "Option B"],
      },
      radio: {
        label: "Pick one",
        options: ["Option A", "Option B"],
      },
      phone: { label: "Phone" },
      email: { label: "Email" },
      address: { label: "Address" },
      ssn: { label: "SSN" },
      signature: { label: "Employee Signature", required: true },
      file_upload: { label: "Attach a file" },
      section_header: { label: "Section title" },
    };
    onChange([
      ...schema,
      {
        id: newFieldId(),
        type,
        required: type === "section_header" ? false : false,
        ...defaults[type],
      } as FormField,
    ]);
  };

  return (
    <div className="space-y-3">
      {schema.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/60 bg-surface px-4 py-8 text-center">
          <p className="text-sm text-text-muted">
            No fields yet. Add fields to build the form employees will fill out.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {schema.map((field, idx) => (
            <li
              key={field.id}
              className="rounded-md border border-border/40 bg-surface px-3 py-3"
            >
              <div className="flex items-start gap-2">
                <GripVertical className="h-4 w-4 text-text-muted mt-1 shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wide text-accent bg-accent/10 px-1.5 py-0.5 rounded">
                      {field.type}
                    </span>
                    {field.required && (
                      <span className="text-[10px] uppercase tracking-wide text-warning bg-warning/10 px-1.5 py-0.5 rounded">
                        Required
                      </span>
                    )}
                  </div>

                  <Input
                    label="Label"
                    value={field.label}
                    onChange={(e) =>
                      updateField(idx, { label: e.target.value })
                    }
                  />

                  {field.type !== "section_header" && (
                    <Input
                      label="Help text (optional)"
                      value={field.help_text ?? ""}
                      onChange={(e) =>
                        updateField(idx, { help_text: e.target.value })
                      }
                      placeholder="Shown below the field"
                    />
                  )}

                  {FIELDS_WITH_OPTIONS.has(field.type) && (
                    <OptionsEditor
                      options={field.options ?? []}
                      onChange={(opts) =>
                        updateField(idx, { options: opts })
                      }
                    />
                  )}

                  {field.type !== "section_header" && (
                    <label className="inline-flex items-center gap-2 text-xs text-text-primary cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(field.required)}
                        onChange={(e) =>
                          updateField(idx, { required: e.target.checked })
                        }
                        className="h-4 w-4 rounded border-border accent-accent"
                      />
                      Required
                    </label>
                  )}
                </div>

                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => moveField(idx, -1)}
                    disabled={idx === 0}
                    className="p-1 rounded hover:bg-surface-hover text-text-muted disabled:opacity-30"
                    title="Move up"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveField(idx, 1)}
                    disabled={idx === schema.length - 1}
                    className="p-1 rounded hover:bg-surface-hover text-text-muted disabled:opacity-30"
                    title="Move down"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeField(idx)}
                    className="p-1 rounded hover:bg-danger/10 text-danger"
                    title="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Add-field popover */}
      <div className="relative">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation();
            setAddOpen((v) => !v);
          }}
        >
          <Plus className="h-4 w-4" />
          Add field
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
        {addOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute z-10 mt-1 w-72 rounded-md border border-border bg-background shadow-lg py-1 max-h-80 overflow-y-auto"
          >
            {FIELD_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => addField(t)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-surface-hover"
              >
                {FIELD_TYPE_LABELS[t]}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OptionsEditor({
  options,
  onChange,
}: {
  options: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div>
      <p className="text-xs text-text-muted mb-1">Options</p>
      <div className="space-y-1">
        {options.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              value={o}
              onChange={(e) => {
                const next = [...options];
                next[i] = e.target.value;
                onChange(next);
              }}
            />
            <button
              type="button"
              onClick={() => onChange(options.filter((_, idx) => idx !== i))}
              className="p-1 rounded hover:bg-danger/10 text-danger"
              title="Remove option"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange([...options, ""])}
        className="mt-1 text-xs text-accent hover:underline"
      >
        + Add option
      </button>
    </div>
  );
}

export default FormBuilder;
